import {
  Add as AddIcon,
  ContentCopy,
  Print as PrintIcon,
  RestartAlt,
  Visibility,
} from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack, IoTrashOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import InvoicePrintThemeToggle from "components/Invoice/InvoicePrintThemeToggle";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import { getCompanyProfile } from "utils/companyService";
import PayslipTemplate from "./PayslipTemplate";

const PAYMENT_METHODS = ["Electronic Transfer", "Cheque", "Cash"];
const FREQUENCIES = ["Weekly", "Fortnightly", "Monthly"];

const EARNING_PRESETS = [
  "Ordinary Hours",
  "Overtime (1.5x)",
  "Overtime (2x)",
  "Annual Leave Taken",
  "Personal/Carer's Leave Taken",
  "Public Holiday Pay",
  "Bonus",
  "Commission",
  "Allowance",
  "Back Pay",
];

const DEDUCTION_PRESETS = [
  "Salary Sacrifice (Super)",
  "Union Fees",
  "Health Insurance",
  "Workplace Giving",
  "Child Support",
  "Other Deduction",
];

const TAX_PRESETS = ["PAYG Withholding", "STSL / HELP", "Medicare Levy Surcharge"];

const SUPER_PRESETS = [
  "Superannuation Guarantee (SGC)",
  "Salary Sacrifice Super",
  "Employer Additional",
];

const LEAVE_PRESETS = ["Annual Leave", "Personal/Carer's Leave", "Long Service Leave"];

const today = () => format(new Date(), "yyyy-MM-dd");

const blankEarning = () => ({
  uid: crypto.randomUUID(),
  description: "Ordinary Hours",
  hours: "",
  rate: "",
  amount: "",
  amountOverridden: false,
  ytd: "",
});

const blankDeduction = () => ({
  uid: crypto.randomUUID(),
  description: "Salary Sacrifice (Super)",
  amount: "",
  ytd: "",
});

const blankTax = () => ({
  uid: crypto.randomUUID(),
  description: "PAYG Withholding",
  amount: "",
  ytd: "",
});

const blankSuper = () => ({
  uid: crypto.randomUUID(),
  description: "Superannuation Guarantee (SGC)",
  rate: 11.5,
  fund: "",
  memberNumber: "",
  amount: "",
  amountOverridden: false,
  ytd: "",
});

const blankLeave = () => ({
  uid: crypto.randomUUID(),
  type: "Annual Leave",
  accrued: "",
  taken: "",
  balance: "",
});

const initialForm = () => ({
  employee: {
    name: "",
    employeeId: "",
    position: "",
    classification: "",
    address: "",
    tfn: "",
    paymentMethod: "Electronic Transfer",
    bsb: "",
    accountNumber: "",
  },
  period: {
    start: today(),
    end: today(),
    paymentDate: today(),
    frequency: "Weekly",
  },
  earningsPriorYtd: "",
  earnings: [blankEarning()],
  deductions: [],
  taxes: [blankTax()],
  supers: [blankSuper()],
  leave: [
    { ...blankLeave(), type: "Annual Leave" },
    { ...blankLeave(), type: "Personal/Carer's Leave" },
  ],
  notes: "",
});

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const SalaryPayslip = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [showPreview, setShowPreview] = useState(true);

  const { data: companyProfile } = useQuery({
    queryKey: ["companyProfile"],
    queryFn: getCompanyProfile,
  });

  const updateField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let cursor = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        cursor[keys[i]] = { ...cursor[keys[i]] };
        cursor = cursor[keys[i]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateRow = (group, uid, patch) => {
    setForm((prev) => ({
      ...prev,
      [group]: prev[group].map((row) => (row.uid === uid ? { ...row, ...patch } : row)),
    }));
  };

  const addRow = (group, factory) => {
    setForm((prev) => ({ ...prev, [group]: [...prev[group], factory()] }));
  };

  const removeRow = (group, uid) => {
    setForm((prev) => ({ ...prev, [group]: prev[group].filter((row) => row.uid !== uid) }));
  };

  // Earnings auto-calc: amount = hours * rate, unless overridden
  const earningsComputed = useMemo(
    () =>
      form.earnings.map((e) => {
        const computed = num(e.hours) * num(e.rate);
        const amount = e.amountOverridden ? num(e.amount) : computed;
        return { ...e, computedAmount: amount };
      }),
    [form.earnings]
  );

  const totals = useMemo(() => {
    const earnings = earningsComputed.reduce((s, e) => s + num(e.computedAmount), 0);
    const earningsYtd = form.earnings.reduce((s, e) => s + num(e.ytd), 0);
    const earningsPriorYtd = num(form.earningsPriorYtd);
    const earningsYtdGrand = earningsYtd + earningsPriorYtd;
    const deductions = form.deductions.reduce((s, d) => s + num(d.amount), 0);
    const deductionsYtd = form.deductions.reduce((s, d) => s + num(d.ytd), 0);
    const taxes = form.taxes.reduce((s, t) => s + num(t.amount), 0);
    const taxesYtd = form.taxes.reduce((s, t) => s + num(t.ytd), 0);
    const supers = form.supers.reduce((s, sg) => {
      const computed = (earnings * num(sg.rate)) / 100;
      const amt = sg.amountOverridden ? num(sg.amount) : computed;
      return s + amt;
    }, 0);
    const supersYtd = form.supers.reduce((s, sg) => s + num(sg.ytd), 0);

    const netPay = earnings - taxes - deductions;
    const netPayYtd = earningsYtdGrand - taxesYtd - deductionsYtd;

    return {
      earnings,
      earningsYtd,
      earningsPriorYtd,
      earningsYtdGrand,
      deductions,
      deductionsYtd,
      taxes,
      taxesYtd,
      supers,
      supersYtd,
      netPay,
      netPayYtd,
    };
  }, [
    earningsComputed,
    form.earnings,
    form.earningsPriorYtd,
    form.deductions,
    form.taxes,
    form.supers,
  ]);

  // Keep super "amount" in sync with rate × earnings unless overridden
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      supers: prev.supers.map((sg) => {
        if (sg.amountOverridden) return sg;
        const computed = (totals.earnings * num(sg.rate)) / 100;
        return { ...sg, amount: computed.toFixed(2) };
      }),
    }));
  }, [totals.earnings]);

  const printData = useMemo(
    () => ({
      company: companyProfile || {},
      employee: form.employee,
      period: form.period,
      earnings: earningsComputed.map((e) => ({
        description: e.description,
        hours: e.hours,
        rate: e.rate,
        amount: e.computedAmount,
        ytd: e.ytd,
      })),
      deductions: form.deductions,
      taxes: form.taxes,
      supers: form.supers.map((sg) => ({
        ...sg,
        amount: sg.amountOverridden ? num(sg.amount) : (totals.earnings * num(sg.rate)) / 100,
      })),
      leave: form.leave,
      totals,
      notes: form.notes,
    }),
    [companyProfile, form, earningsComputed, totals]
  );

  const printRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Payslip-${form.employee.name || "employee"}-${form.period.end || ""}`.trim(),
    pageStyle: getInvoicePrintPageStyle(darkPrint),
  });

  const resetForm = () => setForm(initialForm());

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <IconButton onClick={() => navigate(-1)} size="small">
            <IoArrowBack />
          </IconButton>
          <div>
            <Typography variant="h6" fontWeight={700}>
              Salary Payslip
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Xero-style payslip with earnings, deductions, super, leave and YTD totals.
            </Typography>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <InvoicePrintThemeToggle />
          <Button variant="outlined" startIcon={<RestartAlt />} onClick={resetForm} color="inherit">
            Reset
          </Button>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => setShowPreview((s) => !s)}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print Payslip
          </Button>
        </div>
      </div>

      <div className={`grid gap-6 ${showPreview ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {/* ============== EDITOR ============== */}
        <div className="bg-app-surface rounded-lg shadow-sm border border-app-border p-4 flex flex-col gap-5">
          {/* Employee */}
          <Section title="Employee">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField
                label="Full name"
                size="small"
                value={form.employee.name}
                onChange={(e) => updateField("employee.name", e.target.value)}
                fullWidth
              />
              <TextField
                label="Employee ID"
                size="small"
                value={form.employee.employeeId}
                onChange={(e) => updateField("employee.employeeId", e.target.value)}
                fullWidth
              />
              <TextField
                label="Position / Title"
                size="small"
                value={form.employee.position}
                onChange={(e) => updateField("employee.position", e.target.value)}
                fullWidth
              />
              <TextField
                label="Classification"
                size="small"
                placeholder="e.g. Level 2 - Adult"
                value={form.employee.classification}
                onChange={(e) => updateField("employee.classification", e.target.value)}
                fullWidth
              />
              <TextField
                label="Tax File Number"
                size="small"
                value={form.employee.tfn}
                onChange={(e) => updateField("employee.tfn", e.target.value)}
                fullWidth
              />
              <TextField
                label="Address (one line per row)"
                size="small"
                multiline
                minRows={2}
                placeholder={"1 Queens Avenue\nKogarah NSW 2217"}
                value={form.employee.address}
                onChange={(e) => updateField("employee.address", e.target.value)}
                fullWidth
                className="md:col-span-2"
              />
              <TextField
                label="Payment method"
                size="small"
                select
                value={form.employee.paymentMethod}
                onChange={(e) => updateField("employee.paymentMethod", e.target.value)}
                fullWidth
              >
                {PAYMENT_METHODS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
              <div className="grid grid-cols-2 gap-3">
                <TextField
                  label="BSB"
                  size="small"
                  value={form.employee.bsb}
                  onChange={(e) => updateField("employee.bsb", e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Account number"
                  size="small"
                  value={form.employee.accountNumber}
                  onChange={(e) => updateField("employee.accountNumber", e.target.value)}
                  fullWidth
                />
              </div>
            </div>
          </Section>

          {/* Period */}
          <Section title="Pay period">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <TextField
                label="Period start"
                type="date"
                size="small"
                value={form.period.start}
                onChange={(e) => updateField("period.start", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Period end"
                type="date"
                size="small"
                value={form.period.end}
                onChange={(e) => updateField("period.end", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Payment date"
                type="date"
                size="small"
                value={form.period.paymentDate}
                onChange={(e) => updateField("period.paymentDate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Frequency"
                size="small"
                select
                value={form.period.frequency}
                onChange={(e) => updateField("period.frequency", e.target.value)}
                fullWidth
              >
                {FREQUENCIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Other Previous Earnings (YTD)"
                size="small"
                type="number"
                value={form.earningsPriorYtd}
                onChange={(e) => updateField("earningsPriorYtd", e.target.value)}
                helperText="Optional. Prior YTD earnings to add to the SALARY & WAGES YTD total."
                fullWidth
                className="md:col-span-2"
              />
            </div>
          </Section>

          {/* Earnings */}
          <Section
            title="Earnings"
            actions={
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => addRow("earnings", blankEarning)}
              >
                Add line
              </Button>
            }
          >
            <Stack spacing={1.5}>
              {form.earnings.map((row, i) => {
                const computed = num(row.hours) * num(row.rate);
                return (
                  <div
                    key={row.uid}
                    className="grid grid-cols-12 gap-2 items-center bg-app-surface-muted rounded p-2"
                  >
                    <Autocomplete
                      className="col-span-12 md:col-span-4"
                      freeSolo
                      size="small"
                      options={EARNING_PRESETS}
                      value={row.description}
                      onChange={(_, v) => updateRow("earnings", row.uid, { description: v || "" })}
                      onInputChange={(_, v) => updateRow("earnings", row.uid, { description: v })}
                      renderInput={(params) => <TextField {...params} label={`Earning ${i + 1}`} />}
                    />
                    <TextField
                      label="Hours / Units"
                      size="small"
                      type="number"
                      value={row.hours}
                      onChange={(e) => updateRow("earnings", row.uid, { hours: e.target.value })}
                      className="col-span-4 md:col-span-2"
                    />
                    <TextField
                      label="Rate (AUD)"
                      size="small"
                      type="number"
                      value={row.rate}
                      onChange={(e) => updateRow("earnings", row.uid, { rate: e.target.value })}
                      className="col-span-4 md:col-span-2"
                    />
                    <TextField
                      label="Amount"
                      size="small"
                      type="number"
                      value={row.amountOverridden ? row.amount : computed.toFixed(2)}
                      onChange={(e) =>
                        updateRow("earnings", row.uid, {
                          amount: e.target.value,
                          amountOverridden: true,
                        })
                      }
                      className="col-span-4 md:col-span-2"
                    />
                    <TextField
                      label="YTD"
                      size="small"
                      type="number"
                      value={row.ytd}
                      onChange={(e) => updateRow("earnings", row.uid, { ytd: e.target.value })}
                      className="col-span-8 md:col-span-1"
                    />
                    <div className="col-span-4 md:col-span-1 flex justify-end gap-0.5">
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          onClick={() =>
                            addRow("earnings", () => ({
                              ...blankEarning(),
                              ...row,
                              uid: crypto.randomUUID(),
                            }))
                          }
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={form.earnings.length === 1}
                          onClick={() => removeRow("earnings", row.uid)}
                        >
                          <IoTrashOutline />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </Stack>
          </Section>

          {/* Deductions */}
          <Section
            title="Deductions"
            actions={
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => addRow("deductions", blankDeduction)}
              >
                Add deduction
              </Button>
            }
          >
            {form.deductions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No deductions for this pay run.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {form.deductions.map((row) => (
                  <div
                    key={row.uid}
                    className="grid grid-cols-12 gap-2 items-center bg-app-surface-muted rounded p-2"
                  >
                    <Autocomplete
                      className="col-span-12 md:col-span-6"
                      freeSolo
                      size="small"
                      options={DEDUCTION_PRESETS}
                      value={row.description}
                      onChange={(_, v) =>
                        updateRow("deductions", row.uid, { description: v || "" })
                      }
                      onInputChange={(_, v) => updateRow("deductions", row.uid, { description: v })}
                      renderInput={(params) => <TextField {...params} label="Description" />}
                    />
                    <TextField
                      label="Amount"
                      size="small"
                      type="number"
                      value={row.amount}
                      onChange={(e) => updateRow("deductions", row.uid, { amount: e.target.value })}
                      className="col-span-6 md:col-span-2"
                    />
                    <TextField
                      label="YTD"
                      size="small"
                      type="number"
                      value={row.ytd}
                      onChange={(e) => updateRow("deductions", row.uid, { ytd: e.target.value })}
                      className="col-span-4 md:col-span-3"
                    />
                    <div className="col-span-2 md:col-span-1 flex justify-end">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRow("deductions", row.uid)}
                      >
                        <IoTrashOutline />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </Stack>
            )}
          </Section>

          {/* Taxes */}
          <Section
            title="Taxes"
            actions={
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => addRow("taxes", blankTax)}
              >
                Add tax line
              </Button>
            }
          >
            <Stack spacing={1.5}>
              {form.taxes.map((row) => (
                <div
                  key={row.uid}
                  className="grid grid-cols-12 gap-2 items-center bg-app-surface-muted rounded p-2"
                >
                  <Autocomplete
                    className="col-span-12 md:col-span-6"
                    freeSolo
                    size="small"
                    options={TAX_PRESETS}
                    value={row.description}
                    onChange={(_, v) => updateRow("taxes", row.uid, { description: v || "" })}
                    onInputChange={(_, v) => updateRow("taxes", row.uid, { description: v })}
                    renderInput={(params) => <TextField {...params} label="Description" />}
                  />
                  <TextField
                    label="Amount (AUD)"
                    size="small"
                    type="number"
                    value={row.amount}
                    onChange={(e) => updateRow("taxes", row.uid, { amount: e.target.value })}
                    className="col-span-6 md:col-span-2"
                  />
                  <TextField
                    label="YTD"
                    size="small"
                    type="number"
                    value={row.ytd}
                    onChange={(e) => updateRow("taxes", row.uid, { ytd: e.target.value })}
                    className="col-span-4 md:col-span-3"
                  />
                  <div className="col-span-2 md:col-span-1 flex justify-end">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={form.taxes.length === 1}
                      onClick={() => removeRow("taxes", row.uid)}
                    >
                      <IoTrashOutline />
                    </IconButton>
                  </div>
                </div>
              ))}
            </Stack>
          </Section>

          {/* Superannuation */}
          <Section
            title="Superannuation"
            actions={
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => addRow("supers", blankSuper)}
              >
                Add super line
              </Button>
            }
          >
            <Stack spacing={1.5}>
              {form.supers.map((row) => {
                const computed = (totals.earnings * num(row.rate)) / 100;
                return (
                  <div
                    key={row.uid}
                    className="grid grid-cols-12 gap-2 items-center bg-app-surface-muted rounded p-2"
                  >
                    <Autocomplete
                      className="col-span-12 md:col-span-4"
                      freeSolo
                      size="small"
                      options={SUPER_PRESETS}
                      value={row.description}
                      onChange={(_, v) => updateRow("supers", row.uid, { description: v || "" })}
                      onInputChange={(_, v) => updateRow("supers", row.uid, { description: v })}
                      renderInput={(params) => <TextField {...params} label="Description" />}
                    />
                    <TextField
                      label="Rate (%)"
                      size="small"
                      type="number"
                      value={row.rate}
                      onChange={(e) => updateRow("supers", row.uid, { rate: e.target.value })}
                      className="col-span-4 md:col-span-1"
                    />
                    <TextField
                      label="Fund"
                      size="small"
                      value={row.fund}
                      onChange={(e) => updateRow("supers", row.uid, { fund: e.target.value })}
                      className="col-span-6 md:col-span-2"
                    />
                    <TextField
                      label="Member #"
                      size="small"
                      value={row.memberNumber}
                      onChange={(e) =>
                        updateRow("supers", row.uid, { memberNumber: e.target.value })
                      }
                      className="col-span-6 md:col-span-2"
                    />
                    <TextField
                      label="Amount"
                      size="small"
                      type="number"
                      value={row.amountOverridden ? row.amount : computed.toFixed(2)}
                      onChange={(e) =>
                        updateRow("supers", row.uid, {
                          amount: e.target.value,
                          amountOverridden: true,
                        })
                      }
                      className="col-span-6 md:col-span-1"
                    />
                    <TextField
                      label="YTD"
                      size="small"
                      type="number"
                      value={row.ytd}
                      onChange={(e) => updateRow("supers", row.uid, { ytd: e.target.value })}
                      className="col-span-4 md:col-span-1"
                    />
                    <div className="col-span-2 md:col-span-1 flex justify-end">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={form.supers.length === 1}
                        onClick={() => removeRow("supers", row.uid)}
                      >
                        <IoTrashOutline />
                      </IconButton>
                    </div>
                  </div>
                );
              })}
            </Stack>
          </Section>

          {/* Leave */}
          <Section
            title="Leave balances"
            actions={
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => addRow("leave", blankLeave)}
              >
                Add leave type
              </Button>
            }
          >
            <Stack spacing={1.5}>
              {form.leave.map((row) => (
                <div
                  key={row.uid}
                  className="grid grid-cols-12 gap-2 items-center bg-app-surface-muted rounded p-2"
                >
                  <Autocomplete
                    className="col-span-12 md:col-span-4"
                    freeSolo
                    size="small"
                    options={LEAVE_PRESETS}
                    value={row.type}
                    onChange={(_, v) => updateRow("leave", row.uid, { type: v || "" })}
                    onInputChange={(_, v) => updateRow("leave", row.uid, { type: v })}
                    renderInput={(params) => <TextField {...params} label="Leave type" />}
                  />
                  <TextField
                    label="Accrued (hrs)"
                    size="small"
                    type="number"
                    value={row.accrued}
                    onChange={(e) => updateRow("leave", row.uid, { accrued: e.target.value })}
                    className="col-span-4 md:col-span-2"
                  />
                  <TextField
                    label="Taken (hrs)"
                    size="small"
                    type="number"
                    value={row.taken}
                    onChange={(e) => updateRow("leave", row.uid, { taken: e.target.value })}
                    className="col-span-4 md:col-span-2"
                  />
                  <TextField
                    label="Balance (hrs)"
                    size="small"
                    type="number"
                    value={row.balance}
                    onChange={(e) => updateRow("leave", row.uid, { balance: e.target.value })}
                    className="col-span-4 md:col-span-3"
                  />
                  <div className="col-span-12 md:col-span-1 flex justify-end">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeRow("leave", row.uid)}
                    >
                      <IoTrashOutline />
                    </IconButton>
                  </div>
                </div>
              ))}
            </Stack>
          </Section>

          {/* Notes */}
          <Section title="Notes (optional)">
            <TextField
              size="small"
              multiline
              minRows={2}
              fullWidth
              placeholder="Any message to print on the payslip"
              value={form.notes}
              onChange={(e) => updateField("notes", e.target.value)}
            />
          </Section>

          <Divider />

          {/* Live totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryStat label="Gross" value={totals.earnings} />
            <SummaryStat label="Tax" value={totals.taxes} negative />
            <SummaryStat label="Deductions" value={totals.deductions} negative />
            <SummaryStat label="Net Pay" value={totals.netPay} highlight />
            <SummaryStat label="Super" value={totals.supers} muted />
          </div>
        </div>

        {/* ============== PREVIEW ============== */}
        {showPreview && (
          <div className="rounded-lg shadow-sm border border-app-border overflow-auto bg-app-surface-muted p-4">
            <div style={{ transformOrigin: "top left", minWidth: 720 }}>
              <PayslipTemplate data={printData} />
            </div>
          </div>
        )}
      </div>

      {/* Hidden printable */}
      <div style={{ position: "absolute", left: -9999, top: -9999 }}>
        <PayslipTemplate ref={printRef} data={printData} />
      </div>
    </div>
  );
};

const Section = ({ title, actions, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <Typography variant="subtitle2" fontWeight={700} className="!uppercase tracking-wide">
        {title}
      </Typography>
      {actions}
    </div>
    {children}
  </div>
);

const SummaryStat = ({ label, value, negative, highlight, muted }) => {
  const tone = highlight
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : muted
    ? "text-app-text bg-app-surface-muted border-app-border"
    : negative
    ? "text-rose-700 bg-rose-50 border-rose-200"
    : "text-app-text bg-app-surface border-app-border";
  return (
    <div className={`border rounded-lg px-3 py-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-bold">
        {negative ? "-" : ""}$
        {Number(value || 0).toLocaleString("en-AU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>
    </div>
  );
};

export default SalaryPayslip;
